import { ApolloError } from '@apollo/client'
import { catchError, combineLatest, map, Observable, retry, switchMap, timer } from 'rxjs'

import { ParentProductsDocument } from '../../../graphql/api'
import {
  ConfigurableProductDetailsFragment,
  GetProductsByUrlKeyDocument,
} from '../../../graphql/magento'
import { logAndCaptureException } from '../../../utils/errorTools'
import { left, Result, right } from '../../../utils/result'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { fromObservableQuery } from '../../../utils/rx/observable-query'
import { mapRight, tapLeft } from '../../../utils/rx/operators'
import { latestAccessToken$ } from '../../auth/state'
import { latestApolloClient$ } from '../../graphql/apollo'
import { ParentProductSelectionForModal } from './types'

export const latestApolloClientAndToken$ = combineLatest([
  latestApolloClient$,
  latestAccessToken$,
]).pipe(map(([client, token]) => ({ client, token })))

export const sanityParentProductQuery = (
  sku: string,
): Observable<Result<ParentProductSelectionForModal, ApolloError | Error>> =>
  latestApolloClient$.pipe(
    switchMap((client) =>
      fromObservableQuery(
        client.watchQuery({
          query: ParentProductsDocument,
          context: { uri: '/api/graphql' },
          // override global error policy
          errorPolicy: 'none',
          variables: { sku },
        }),
      ).pipe(
        retry({
          count: 3,
          delay: (_, count) =>
            // retry with exponential backoff and max of 30 seconds
            timer(Math.min(30000, 2 ^ (count * 1000))),
        }),
      ),
    ),
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server)
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    catchError(coerceCaughtToLeft),
    tapLeft(({ error }) => logAndCaptureException(error)),
    mapRight(({ data }) => {
      const [parentProduct] = data.allParentProduct ?? []
      return parentProduct && parentProduct.sku != null && parentProduct.sku === sku
        ? right(parentProduct as ParentProductSelectionForModal)
        : left(new Error(`Parent product not found: ${sku}`))
    }),
  )

export const configurableProductByUrlKey = (
  urlKey: string,
): Observable<Result<ConfigurableProductDetailsFragment, ApolloError | Error>> =>
  latestApolloClientAndToken$.pipe(
    switchMap(({ client, token }) =>
      fromObservableQuery(
        client.watchQuery({
          query: GetProductsByUrlKeyDocument,
          variables: { urlKey },
          context: { ...(token && { token }) },
          // override global error policy
          errorPolicy: 'none',
          fetchPolicy: 'cache-first',
        }),
      ).pipe(
        retry({
          count: 3,
          delay: (_, count) =>
            // retry with exponential backoff and max of 30 seconds
            timer(Math.min(30000, 2 ^ (count * 1000))),
        }),
      ),
    ),
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server)
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    catchError(coerceCaughtToLeft),
    tapLeft(({ error }) => logAndCaptureException(error)),
    mapRight(({ data }) =>
      data?.products?.items?.[0]?.__typename === 'ConfigurableProduct'
        ? right(data.products.items[0])
        : left(new Error('Product not found')),
    ),
  )
